import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { BasicHeader } from './components/BasicHeader';
import { useEffect } from 'react';
import { AppHeaderOverrideState, IAppHeaderOverrideProps } from './AppHeaderOverrideState';

export const HEADER_HEIGHT = 57;
export const HEADER_Z_INDEX = 10;

/**
 * Hands the page's title and tabs to the app header, following them as they change: the care
 * section's tabs link to whichever care map is active, and staff switch between clients without
 * leaving the page.
 */
export const AppHeaderOverride: React.FC<IAppHeaderOverrideProps> = ({ title, hidden, tabs, hidesBeaButton }) => {
    const override = useInstance(AppHeaderOverrideState);

    // Pages build their tabs afresh on every render, so they count as changed by what they show and
    // link to, not by identity.
    const tabsKey = tabs && JSON.stringify([tabs.tabGroupId, tabs.tabs.map(({ label, url, dataId }) => [label, url, dataId])]);

    useEffect(() => {
        override.set({ title, hidden, tabs, hidesBeaButton });
        return () => {
            override.clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [override, title, hidden, tabsKey, hidesBeaButton]);

    return null;
};

export const AppHeader: React.FC<{title?: string | React.ReactNode}> = ({ title }) => {
    const override = useInstance(AppHeaderOverrideState);
    useSubscription(() => override);

    title = override.title || title || '';

    if (override.hidden) {
        return null;
    }

    return (
        <BasicHeader tabs={override.tabs}
            hidesBeaButton={override.hidesBeaButton}
            title={(
                <span data-id="app-header-title">{title}</span>
            )}
        >
        </BasicHeader>
    );
};
